/**
 * Écran de succès du déploiement (§3.3) : confettis, coche animée, URL finale
 * en grand, copie du lien et QR code.
 *
 * Le texte s'adapte à la nature du job : restaurer une version antérieure n'est
 * pas mettre en ligne une nouveauté, et annoncer « votre application est en
 * ligne » après un rollback laisserait croire à un déploiement complet.
 */

import type { DeploymentType } from '@portail/shared';
import confetti from 'canvas-confetti';
import { Check, Copy, ExternalLink } from 'lucide-react';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';

import { useClipboard } from '../hooks/useClipboard';
import { formatDuration } from '../lib/format';
import { Button, Card, CopyFeedback } from './ui';

/** Coche tracée en CSS, sans image ni bibliothèque d'animation. */
function AnimatedCheck() {
  return (
    <span className="inline-flex h-16 w-16 animate-pop-in items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950">
      <svg viewBox="0 0 32 32" className="h-9 w-9" role="img" aria-label="Déploiement réussi">
        <path
          d="M7 17 L13.5 23 L25 10"
          fill="none"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="animate-draw-check stroke-emerald-600 dark:stroke-emerald-400"
          style={{ strokeDasharray: 48, strokeDashoffset: 48 }}
        />
      </svg>
    </span>
  );
}

/** Propriétés du panneau de succès. */
export interface SuccessPanelProps {
  url: string;
  slug: string;
  /** Durée totale du déploiement, en millisecondes. */
  durationMs: number | null;
  /** `DEPLOY` (pipeline complet) ou `ROLLBACK` (restauration). */
  type?: DeploymentType;
  /** Numéro de la version désormais en ligne, s'il est connu. */
  versionNumber?: number | null;
}

/** Panneau affiché lorsque le déploiement aboutit. */
export function SuccessPanel({
  url,
  slug,
  durationMs,
  type = 'DEPLOY',
  versionNumber,
}: SuccessPanelProps) {
  const { status, copy } = useClipboard();
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  // Confettis : une seule fois, au passage à l'état `DONE`.
  useEffect(() => {
    void confetti({
      particleCount: 140,
      spread: 75,
      origin: { y: 0.35 },
      disableForReducedMotion: true,
    });
  }, []);

  // QR code de l'URL finale, généré côté client en data-URL.
  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(url, { width: 220, margin: 1, errorCorrectionLevel: 'M' })
      .then((dataUrl) => {
        if (!cancelled) setQrDataUrl(dataUrl);
      })
      .catch(() => {
        // Le QR code est un confort : son échec ne doit rien casser.
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <Card className="p-6">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-4">
            <AnimatedCheck />
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                {type === 'ROLLBACK' ? `« ${slug} » a été restaurée` : `« ${slug} » est en ligne`}
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {type === 'ROLLBACK'
                  ? `Restauration terminée en ${formatDuration(durationMs)}, sans reconstruction : l'image de la version restaurée était déjà dans le registre.`
                  : `Déploiement terminé en ${formatDuration(durationMs)}.`}
                {typeof versionNumber === 'number' ? ` Version v${versionNumber} en ligne.` : null}
              </p>
            </div>
          </div>

          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="mt-6 inline-flex max-w-full items-center gap-2 break-all text-xl font-semibold text-indigo-700 underline decoration-indigo-300 underline-offset-4 hover:text-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:text-indigo-300 dark:decoration-indigo-700"
          >
            {url}
            <ExternalLink className="h-5 w-5 shrink-0" aria-hidden="true" />
          </a>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Button variant="primary" onClick={() => copy(url)}>
              <Copy className="h-4 w-4" aria-hidden="true" />
              Copier le lien
            </Button>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              Ouvrir l'application
            </a>
            <CopyFeedback status={status} />
          </div>

          <p className="mt-5 flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
            L'application est protégée par l'authentification interne et son trafic est mesuré par
            Umami. Retrouvez-la à tout moment depuis « Mes applications ».
          </p>
        </div>

        <div className="shrink-0 text-center">
          {qrDataUrl ? (
            <>
              <img
                src={qrDataUrl}
                alt={`QR code menant à ${url}`}
                width={160}
                height={160}
                className="rounded-md border border-slate-200 bg-white p-2 dark:border-slate-700"
              />
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Scannez pour ouvrir
                <br />
                sur mobile
              </p>
            </>
          ) : (
            <div className="h-[176px] w-[176px] animate-pulse rounded-md bg-slate-200 dark:bg-slate-800" />
          )}
        </div>
      </div>
    </Card>
  );
}
